// Ocean Insight Pro Mobile - Fishing Redux Slice

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { FishPrediction, FishingHotspot, CatchReport, FishSpecies, GeoCoordinate } from '../types';
import { logger } from '../utils/logger';

// Define the fishing state interface
interface FishingState {
  predictions: FishPrediction[];
  hotspots: FishingHotspot[];
  catchReports: CatchReport[];
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

// Initial state
const initialState: FishingState = {
  predictions: [],
  hotspots: [],
  catchReports: [],
  loading: false,
  error: null,
  lastUpdated: null,
};

// Mock fishing predictions for development
const generateMockPredictions = (location: GeoCoordinate): FishPrediction[] => {
  const species: FishSpecies[] = ['mahi-mahi', 'wahoo', 'yellowfin-tuna', 'yellowfin-tuna', 'sailfish'];
  const predictions: FishPrediction[] = [];
  
  for (let i = 0; i < 5; i++) {
    const now = new Date();
    const startTime = new Date(now.getTime() + Math.random() * 24 * 60 * 60 * 1000);
    const endTime = new Date(startTime.getTime() + 4 * 60 * 60 * 1000); // 4 hour window
    
    predictions.push({
      id: `pred-${Date.now()}-${i}`,
      species: species[i % species.length],
      probability: Math.floor((Math.random() * 0.5 + 0.5) * 100), // 50-100%
      location: {
        latitude: location.latitude + (Math.random() - 0.5) * 0.2,
        longitude: location.longitude + (Math.random() - 0.5) * 0.2,
      },
      confidence: ['low', 'medium', 'high'][Math.floor(Math.random() * 3)] as 'low' | 'medium' | 'high',
      timeWindow: {
        start: startTime.toISOString(),
        end: endTime.toISOString(),
      },
      conditions: {
        optimalSSTRange: [75 + Math.random() * 5, 80 + Math.random() * 5] as [number, number],
        optimalChlorophyll: [0.1 + Math.random() * 0.2, 0.5 + Math.random() * 0.3] as [number, number],
        optimalDepth: [50 + Math.random() * 50, 150 + Math.random() * 100] as [number, number],
      },
      reasoning: 'AI analysis shows favorable conditions based on SST gradients and chlorophyll fronts',
    });
  }
  
  return predictions;
};

// Enhanced async thunks using mock predictions
export const fetchFishingPredictions = createAsyncThunk(
  'fishing/fetchPredictions',
  async (params?: {
    location: GeoCoordinate;
    searchRadius?: number;
    targetSpecies?: FishSpecies[];
  }): Promise<FishPrediction[]> => {
    const location = params?.location || { latitude: 27.8506, longitude: -97.0036 }; // Default to Port Aransas
    
    logger.info('Fetching fishing predictions', { location });
    
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    const predictions = generateMockPredictions(location);
    
    // Filter by target species if provided
    if (params?.targetSpecies && params.targetSpecies.length > 0) {
      return predictions.filter(p => params.targetSpecies?.includes(p.species) ?? false);
    }
    
    return predictions;
  }
);

// Mock fishing hotspots
const generateMockHotspots = (): FishingHotspot[] => {
  const hotspots: FishingHotspot[] = [
    {
      id: 'hotspot-1',
      name: 'Destin Canyon Edge',
      location: { latitude: 30.2, longitude: -86.7 },
      radius: 2,
      targetSpecies: ['mahi-mahi', 'wahoo', 'yellowfin-tuna'],
      averageDepth: 400,
      structure: 'current-edge',
      bestTimes: ['dawn', 'dusk', 'incoming tide'],
    },
    {
      id: 'hotspot-2',
      name: 'Venice Lumps',
      location: { latitude: 28.8, longitude: -89.4 },
      radius: 3,
      targetSpecies: ['yellowfin-tuna', 'blue-marlin', 'blackfin-tuna'],
      averageDepth: 650,
      structure: 'seamount',
      bestTimes: ['early morning', 'late afternoon', 'slack tide'],
    },
    {
      id: 'hotspot-3',
      name: 'Marathon Hump',
      location: { latitude: 24.6, longitude: -80.9 },
      radius: 1.5,
      targetSpecies: ['sailfish', 'yellowfin-tuna', 'mahi-mahi'],
      averageDepth: 225,
      structure: 'seamount',
      bestTimes: ['dawn', 'dusk', 'outgoing tide'],
    },
  ];
  
  return hotspots;
};

export const fetchFishingHotspots = createAsyncThunk(
  'fishing/fetchHotspots',
  async (params?: {
    region?: { north: number; south: number; east: number; west: number };
    species?: FishSpecies[];
  }): Promise<FishingHotspot[]> => {
    logger.info('Fetching fishing hotspots');
    
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 800));
    
    let hotspots = generateMockHotspots();
    
    // Filter by species if provided
    if (params?.species && params.species.length > 0) {
      hotspots = hotspots.filter((hotspot) =>
        hotspot.targetSpecies.some((target) => params.species?.includes(target) ?? false)
      );
    }
    
    // Filter by region if provided
    if (params?.region) {
      const { north, south, east, west } = params.region;
      hotspots = hotspots.filter(
        (h) =>
          h.location.latitude <= north &&
          h.location.latitude >= south &&
          h.location.longitude <= east &&
          h.location.longitude >= west
      );
    }
    
    return hotspots;
  }
);

// Mock catch reports
const SAMPLE_CATCH_REPORTS: CatchReport[] = [
  {
    id: 'catch-1',
    userId: 'user-1',
    species: 'mahi-mahi',
    count: 3,
    averageSize: 35,
    location: { latitude: 30.2, longitude: -86.7 },
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    conditions: {
      waterTemp: 25.5, // Celsius
      weather: 'Partly cloudy',
      windSpeed: 12,
      waveHeight: 3,
    },
    method: 'trolling',
    depth: 250,
    notes: 'Found at weed line, aggressive bite on ballyhoo',
  },
  {
    id: 'catch-2',
    userId: 'user-1',
    species: 'wahoo',
    count: 1,
    averageSize: 65,
    location: { latitude: 28.8, longitude: -89.4 },
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    conditions: {
      waterTemp: 24.5, // Celsius
      weather: 'Clear',
      windSpeed: 8,
      waveHeight: 2,
    },
    method: 'trolling',
    depth: 400,
    notes: 'High speed trolling with wire leader',
  },
];

export const fetchCatchReports = createAsyncThunk(
  'fishing/fetchCatchReports',
  async (params?: { species?: FishSpecies; days?: number }): Promise<CatchReport[]> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 800));

    let reports = [...SAMPLE_CATCH_REPORTS];

    // Filter by species if provided
    if (params?.species) {
      reports = reports.filter((report) => report.species === params.species);
    }

    // Filter by date range if provided
    if (params?.days !== undefined && params.days > 0) {
      const cutoffDate = new Date(Date.now() - params.days * 24 * 60 * 60 * 1000);
      reports = reports.filter((report) => new Date(report.timestamp) >= cutoffDate);
    }

    return reports;
  }
);

export const submitCatchReport = createAsyncThunk(
  'fishing/submitCatchReport',
  async (report: Omit<CatchReport, 'id' | 'timestamp'>): Promise<CatchReport> => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1200));

    // Create new report with ID and timestamp
    const newReport: CatchReport = {
      ...report,
      id: `catch-${Date.now()}`,
      timestamp: new Date().toISOString(),
    };

    // In a real app, this would send the report to the server
    // and help improve AI predictions
    logger.info('Submitting catch report to improve AI predictions', { reportId: newReport.id });

    return newReport;
  }
);

// New action for refreshing predictions with different parameters
export const refreshPredictionsForLocation = createAsyncThunk(
  'fishing/refreshPredictionsForLocation',
  async (params: {
    location: GeoCoordinate;
    searchRadius?: number;
    targetSpecies?: FishSpecies[];
  }): Promise<FishPrediction[]> => {
    logger.info('Refreshing predictions for new location', { location: params.location });

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    const predictions = generateMockPredictions(params.location);
    
    // Filter by target species if provided
    if (params.targetSpecies && params.targetSpecies.length > 0) {
      return predictions.filter(p => params.targetSpecies?.includes(p.species) ?? false);
    }

    return predictions;
  }
);

// Fishing slice
const fishingSlice = createSlice({
  name: 'fishing',
  initialState,
  reducers: {
    clearFishingData: (state) => {
      state.predictions = [];
      state.hotspots = [];
      state.catchReports = [];
      state.error = null;
    },
    setFishingError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    addCatchReport: (state, action: PayloadAction<CatchReport>) => {
      state.catchReports.unshift(action.payload); // Add to beginning of array
    },
    updatePredictionConfidence: (
      state,
      action: PayloadAction<{ predictionId: string; newConfidence: 'low' | 'medium' | 'high' }>
    ) => {
      const prediction = state.predictions.find((p) => p.id === action.payload.predictionId);
      if (prediction) {
        prediction.confidence = action.payload.newConfidence;
      }
    },
    setCatchReports: (state, action: PayloadAction<CatchReport[]>) => {
      // Replace entire array to avoid duplicates
      state.catchReports = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch fishing predictions
    builder
      .addCase(fetchFishingPredictions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFishingPredictions.fulfilled, (state, action) => {
        state.loading = false;
        state.predictions = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchFishingPredictions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch fishing predictions';
      });

    // Refresh predictions for location
    builder
      .addCase(refreshPredictionsForLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshPredictionsForLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.predictions = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(refreshPredictionsForLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to refresh predictions';
      });

    // Fetch fishing hotspots
    builder
      .addCase(fetchFishingHotspots.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFishingHotspots.fulfilled, (state, action) => {
        state.loading = false;
        state.hotspots = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchFishingHotspots.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch fishing hotspots';
      });

    // Fetch catch reports
    builder
      .addCase(fetchCatchReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCatchReports.fulfilled, (state, action) => {
        state.loading = false;
        state.catchReports = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchCatchReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch catch reports';
      });

    // Submit catch report
    builder
      .addCase(submitCatchReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitCatchReport.fulfilled, (state, action) => {
        state.loading = false;
        state.catchReports.unshift(action.payload);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(submitCatchReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to submit catch report';
      });
  },
});

// Export actions
export const { clearFishingData, setFishingError, addCatchReport, updatePredictionConfidence, setCatchReports } =
  fishingSlice.actions;

// Export reducer
export default fishingSlice.reducer;
